import { Button, Card, Heading, IconButton, Input, Text, toast } from "@overmap-ai/blocks"
import {
	type IssueType,
	OrganizationAccessLevel,
	type Stored,
	ABBREVIATION_MAX_LENGTH,
	ABBREVIATION_MIN_LENGTH,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectIssueTypeById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { useTextConfirmationDialog } from "../../contexts/hooks"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/issue-types/$issueTypeId")

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.settings,
		label: "General",
		description: "Description and broader settings",
		from: "/$organizationId/settings/issue-types/$issueTypeId",
		to: "/$organizationId/settings/issue-types/$issueTypeId/general",
	},
	{
		icon: modelIconMapping.status,
		label: "Statuses",
		description: "Manage issue type statuses",
		from: "/$organizationId/settings/issue-types/$issueTypeId",
		to: "/$organizationId/settings/issue-types/$issueTypeId/statuses",
	},
	{
		icon: modelIconMapping.priority,
		label: "Priorities",
		description: "Manage issue type priorities",
		from: "/$organizationId/settings/issue-types/$issueTypeId",
		to: "/$organizationId/settings/issue-types/$issueTypeId/priorities",
	},
	{
		icon: modelIconMapping.identifier,
		label: "Identifiers",
		description: "Manage identifiers such as QR codes and barcodes",
		from: "/$organizationId/settings/issue-types/$issueTypeId",
		to: "/$organizationId/settings/issue-types/$issueTypeId/identifiers",
	},
	{
		icon: modelIconMapping.form,
		label: "Fields",
		description: "Manage issue type fields",
		from: "/$organizationId/settings/issue-types/$issueTypeId",
		to: "/$organizationId/settings/issue-types/$issueTypeId/fields",
	},
	{
		icon: modelIconMapping.attachment,
		label: "Attachments",
		description: "Manage issue type attachments",
		from: "/$organizationId/settings/issue-types/$issueTypeId",
		to: "/$organizationId/settings/issue-types/$issueTypeId/attachments",
	},
]

export const IssueTypeSettingsDetailView = memo(() => {
	const { issueTypeId } = routeApi.useParams()
	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`expected issueType to exist, got ${issueType}.`)
	}

	const navigate = useNavigate()
	const openTextConfirmationDialog = useTextConfirmationDialog()
	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handlePatchIssueType = useCallback(
		(fields: Partial<Stored<IssueType>>) => {
			const [_, promise] = sdk.issueTypes.update({ uuid: issueType.uuid, ...fields })

			promise.catch((error) => {
				toast.error({
					title: "Failed to update issue type",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[issueType.uuid],
	)

	const handleIssueTypeNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const issueTypeName = e.target.value

			handlePatchIssueType({ name: issueTypeName })
		},
		[handlePatchIssueType],
	)

	const handleDelete = useCallback(() => {
		openTextConfirmationDialog((close) => ({
			title: `Delete ${issueType.name}`,
			description: "Are you sure you want to delete this issue type?",
			confirmString: "delete",
			confirmMessage: "Type 'delete' to confirm",
			actionLabel: "Delete",
			onClose: close,
			onConfirm: async () => {
				await navigate({
					from: "/$organizationId/settings/issue-types/$issueTypeId",
					to: "/$organizationId/settings/issue-types",
				})
				sdk.issueTypes.delete(issueType.uuid).catch((error) => {
					toast.error({
						title: "Failed to delete issue type",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
			},
		}))
	}, [issueType.name, issueType.uuid, navigate, openTextConfirmationDialog])

	const issueTypeNameInputProps = useBlurInput<HTMLInputElement>(issueType.name, handleIssueTypeNameInputBlur, true)

	const handleAbbrInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value
			handlePatchIssueType({ abbr: value })
		},
		[handlePatchIssueType],
	)

	const abbrInputProps = useBlurInput<HTMLInputElement>(issueType.abbr, handleAbbrInputBlur, true)

	const handleColorChange = useCallback(
		(color: string | null) => {
			if (!color) return
			handlePatchIssueType({ color: color })
		},
		[handlePatchIssueType],
	)

	const handleIconChange = useCallback(
		(icon: string | null) => {
			if (!icon) return
			handlePatchIssueType({ icon })
		},
		[handlePatchIssueType],
	)

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{issueType.name || "Unnamed issue type"}</Heading>

					<Text size="sm" accentColor="base">
						Manage issue type settings and fields.
					</Text>
				</div>

				<Card className="flex w-full flex-col gap-2" size="sm">
					<div className="flex w-full flex-col gap-4 sm:flex-row sm:gap-2">
						<div className="flex w-full grow flex-col gap-1">
							<Text accentColor="base" size="sm">
								Icon & name
							</Text>
							<div className="flex w-full gap-2">
								<IconColorPickerPopover
									className="w-[340px]"
									selectedIcon={issueType.icon}
									color={issueType.color}
									onColorChange={handleColorChange}
									onIconChange={handleIconChange}
									size="sm"
								>
									<IconButton
										disabled={!isOrganizationAdmin}
										aria-label="icon"
										size="md"
										accentColor="base"
										variant="soft"
									>
										<LuDynamicIcon icon={issueType.icon as IconType} color={issueType.color} />
									</IconButton>
								</IconColorPickerPopover>
								<Input.Root className="grow" variant="outline">
									<Input.Field
										disabled={!isOrganizationAdmin}
										placeholder="Enter a name"
										{...issueTypeNameInputProps}
									/>
								</Input.Root>
							</div>
						</div>
						<div className="flex w-full grow flex-col gap-1">
							<Text accentColor="base" size="sm">
								Abbreviation
							</Text>
							<Input.Root className="grow" variant="outline">
								<Input.Field
									minLength={ABBREVIATION_MIN_LENGTH}
									maxLength={ABBREVIATION_MAX_LENGTH}
									placeholder="Enter an abbreviation"
									disabled={!isOrganizationAdmin}
									{...abbrInputProps}
								/>
							</Input.Root>
						</div>
					</div>
				</Card>

				<RouteLinksCard routes={routes} />

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<Button className="w-max" size="sm" accentColor="danger" onClick={handleDelete}>
						Delete
					</Button>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})
IssueTypeSettingsDetailView.displayName = "IssueTypeSettingsDetailView"
